import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SearchInput } from '@c2fo/liquidity';
import { ArrowUpRightFromSquareIcon, TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import NoDataState from '@/enterprise/components/NoDataState';
import { Table, TableBody, TableDisplaySkeletonRows, TablePagination } from '@/enterprise/components/Table';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { useSearchDraft } from '@/utils/useSearchDraft';
import useUrlState from '@/utils/useUrlState';
import SubstitutionsTable from './SubstitutionsTable';
import { SubstitutionInvoiceActions, SubstitutionInvoiceSource, SubstitutionInvoiceUrlState } from './types';

const DEFAULT_PAGE_SIZE = 100;

const SubstitutionsView = ({
  source,
  actions,
}: {
  source: SubstitutionInvoiceSource;
  actions?: SubstitutionInvoiceActions;
}) => {
  const { t } = useTranslation();
  const [hasNextPage, setHasNextPage] = useState(false);

  const [urlState, setUrlState] = useUrlState<SubstitutionInvoiceUrlState>({
    pageSize: DEFAULT_PAGE_SIZE,
    page: 1,
  });

  const pageSize = urlState.pageSize ?? DEFAULT_PAGE_SIZE;
  const page = urlState.page ?? 1;
  // useUrlState parses digit-only values as numbers (e.g. "123" -> 123), so normalize to a string
  const appliedSearchValue = urlState.searchValue != null ? String(urlState.searchValue) : undefined;

  const {
    draft: searchInput,
    setDraft: setSearchInput,
    inputKey: searchInputKey,
  } = useSearchDraft(appliedSearchValue ?? '');

  const handleSearchValue = (search: string | undefined) => {
    const trimmedSearch = search != null ? String(search).trim() || undefined : undefined;

    actions?.onSearch?.({ searchValue: trimmedSearch });
    setUrlState((f) => ({ ...f, searchValue: trimmedSearch, page: 1 }));
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      actions?.onPageChange?.();
      setUrlState((f) => ({ ...f, page: page + 1 }));
    }
  };

  const handlePreviousPage = () => {
    actions?.onPageChange?.();
    setUrlState((f) => ({ ...f, page: Math.max(1, page - 1) }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = Number(e.target.value);
    actions?.onPageSizeChange?.({ size });
    setUrlState((f) => ({ ...f, pageSize: size, page: 1 }));
  };

  return (
    <div>
      <div className="border-y border-deepGray-500/[12%] bg-canvas">
        {/* Reason: the row owns the padding and gap so a wrapped details button sits one gap-3 below
            the search, not two stacked p-4 paddings apart */}
        <div className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-center">
          {/* Search Bar */}
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <SearchInput
              key={searchInputKey}
              className="w-full sm:w-60"
              onChange={(e) => setSearchInput(e.target.value)}
              onClear={() => {
                setSearchInput('');
                handleSearchValue(undefined);
              }}
              placeholder={t('invoices.searchInputPlaceholder')}
              value={searchInput}
              withSearchIcon
              onKeyDown={(e) => e.key === 'Enter' && handleSearchValue(searchInput)}
            />
            <Button
              className="w-full bg-canvas sm:w-fit"
              onClick={() => handleSearchValue(searchInput)}
              variant="secondary"
            >
              {t('core.search')}
            </Button>
          </div>
          <div className="flex items-center">
            {/* Reason: placeholder until the Substitution Records page (TRF-135) exists. A plain button
                with no handler can't navigate or open a tab; wire it to the deep-link helper then. */}
            <Button className="w-full sm:w-fit" type="button" variant="tertiary">
              <span className="flex items-center gap-2">
                {t('invoiceCentral.viewSubstitutionDetails')}
                <ArrowUpRightFromSquareIcon aria-hidden />
              </span>
            </Button>
          </div>
        </div>
      </div>
      <QueryBoundaries
        LoadingComponent={() => (
          <Table>
            <TableBody>
              <TableDisplaySkeletonRows colSpan={4} rowCount={10} />
            </TableBody>
          </Table>
        )}
        ErrorComponent={() => (
          <div className="px-4 pb-6 pt-8">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
      >
        <SubstitutionsTable
          setHasNextPage={setHasNextPage}
          urlState={{ pageSize, page, searchValue: appliedSearchValue }}
          source={source}
        />
      </QueryBoundaries>
      <TablePagination
        className="p-6"
        onNextChange={handleNextPage}
        onPrevChange={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
        pageSize={pageSize}
        disablePrev={page <= 1}
        disableNext={!hasNextPage}
        currentPage={page}
      />
    </div>
  );
};

export default SubstitutionsView;
